import React from 'react';
import { Analysis } from '../../types';
import { analysisPath, ViewerTab } from '../../App';
import AnalysisThumbnail from '../AnalysisThumbnail';
import { getThumbnailUrl, getChartUrl, getAnimationUrl } from '../MyAnalysesPage/functions';
import { ConversationGroup } from './types';
import './styles.scss';

export type { ConversationGroup, TimelineItem } from './types';
export { buildTimeline } from './types';

interface ConversationTimelineProps {
  conversation: ConversationGroup;
  /** Which page the deep links should point to. */
  scope: 'community' | 'myanalyses';
  /** Label for the user bubbles — "You" on My Analyses, the author on Community. */
  userLabel?: string;
  onBack: () => void;
  backLabel?: string;
  onOpenAnalysis: (analysis: Analysis, tab: ViewerTab) => void;
  /** Extra buttons rendered on the right of the header (copy link, like, share…). */
  headerActions?: React.ReactNode;
  /** Line under the title; defaults to location • count • date. */
  subtitle?: React.ReactNode;
  /** Full shareable URL strip rendered under the header (see <Permalink />). */
  permalink?: React.ReactNode;
}

/**
 * The chat-style timeline of one conversation: user/assistant messages
 * interleaved with each analysis and its Map/Chart (or Animation/Preview)
 * thumbnail cards. Shared by My Analyses and Community so a shared
 * conversation looks exactly the same to visitors as it does to its owner.
 */
const ConversationTimeline: React.FC<ConversationTimelineProps> = ({
  conversation,
  scope,
  userLabel = 'You',
  onBack,
  backLabel = '← Back to List',
  onOpenAnalysis,
  headerActions,
  subtitle,
  permalink,
}) => {
  const timeline = conversation.timeline || [];

  const resultCard = (analysis: Analysis, tab: ViewerTab, src: string, chip: string, label: string, icon: string, alt: string) => (
    <a
      href={analysisPath(analysis.session_id, scope, tab)}
      onClick={(e) => { e.preventDefault(); onOpenAnalysis(analysis, tab); }}
      className="analysis-result-card"
    >
      <div className="result-card-preview">
        <AnalysisThumbnail item={analysis} src={src} alt={alt} fallbackIcon={icon} />
        <span className="result-card-chip">{chip}</span>
      </div>
      <div className="result-card-label"><strong>{label}</strong></div>
    </a>
  );

  return (
    <div className="page-container-full">
      <div className="viewer-header">
        <button onClick={onBack} className="btn btn-secondary">{backLabel}</button>
        <div className="conversation-header-info">
          <h2>💬 {conversation.first_query}</h2>
          <div className="conversation-header-subtitle">
            {subtitle ?? (
              <>
                📍 {conversation.location} • {conversation.count} {conversation.count === 1 ? 'analysis' : 'analyses'} • {new Date(conversation.created_at).toLocaleDateString()}
              </>
            )}
          </div>
        </div>
        {headerActions}
      </div>
      {permalink}

      <div className="viewer-content conversation-viewer-content">
        <div className="timeline-container">
          {timeline.length === 0 && (
            <div className="timeline-empty">No messages recorded for this conversation.</div>
          )}
          {timeline.map((item, index) => {
            if (item.type === 'message') {
              const isUser = item.role === 'user';
              return (
                <div key={`msg-${index}`} className={`timeline-message ${isUser ? 'timeline-message-user' : 'timeline-message-assistant'}`}>
                  <div className={`timeline-avatar ${isUser ? 'timeline-avatar-user' : 'timeline-avatar-assistant'}`}>
                    {isUser ? '👤' : '🛰️'}
                  </div>
                  <div className={`message-bubble ${isUser ? 'message-bubble-user' : 'message-bubble-assistant'}`}>
                    <div className={`message-header ${isUser ? 'message-header-user' : 'message-header-assistant'}`}>
                      {isUser ? userLabel : 'LLEO'} • <span className="message-time">{item.timestamp.toLocaleTimeString()}</span>
                    </div>
                    <div className="message-content">{item.content}</div>
                  </div>
                </div>
              );
            }
            if (item.type !== 'analysis') return null;
            const analysis = item as unknown as Analysis & { timestamp: Date };
            const isAnimation = analysis.analysis_type === 'video_animation';
            return (
              <div key={`analysis-${index}`} className={`timeline-analysis ${index < timeline.length - 1 ? 'timeline-analysis-bordered' : ''}`}>
                <div className="timeline-avatar timeline-avatar-analysis">📊</div>
                <div className="analysis-info-card">
                  <div className="analysis-info-title">Analysis: {(analysis.analysis_type || analysis.query || '').replace(/_/g, ' ')}</div>
                  <div className="analysis-info-date">{analysis.timestamp.toLocaleString()}</div>
                </div>
                {analysis.artifacts_expired ? (
                  <div className="analysis-info-card analysis-expired-note">
                    Result files for this analysis are no longer available.
                  </div>
                ) : (
                  <div className="analysis-results-grid">
                    {isAnimation ? (
                      <>
                        {resultCard(analysis, 'map', getAnimationUrl(analysis), '🎬 GIF', 'Animation', '🎬', 'Animation')}
                        {resultCard(analysis, 'charts', getChartUrl(analysis), '🖼️ Still', 'Preview', '🖼️', 'Preview')}
                      </>
                    ) : (
                      <>
                        {analysis.map_url && resultCard(analysis, 'map', getThumbnailUrl(analysis), '🗺️ Interactive', 'Map View', '🗺️', 'Map preview')}
                        {analysis.chart_url && resultCard(analysis, 'charts', getChartUrl(analysis), '📊 Charts', 'Chart View', '📊', 'Chart preview')}
                      </>
                    )}
                    <a
                      href={analysisPath(analysis.session_id, scope, 'details')}
                      onClick={(e) => { e.preventDefault(); onOpenAnalysis(analysis, 'details'); }}
                      className="analysis-result-card"
                    >
                      <div className="result-card-preview result-card-preview-icon">
                        <span className="result-card-icon">📋</span>
                        <span className="result-card-chip">Inputs & metadata</span>
                      </div>
                      <div className="result-card-label"><strong>Details</strong></div>
                    </a>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ConversationTimeline;
